import useAppointments from "../hooks/use-appointments.hook";
import { isEqual, isValid, isWithinInterval } from "date-fns";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { getDateTime } from "utils/common";

const ReservedAppointmentTypesContext = createContext<any>(null);

type ReservedAppointmentTypesProviderProps = {
  children?: React.ReactNode;
};

export function ReservedAppointmentTypesProvider({
  children,
}: ReservedAppointmentTypesProviderProps) {
  const { appointments } = useAppointments();

  const [appointmentStartDate, setAppointmentStartDate] = useState<
    number | undefined
  >();
  const [appointmentEndDate, setAppointmentEndDate] = useState<
    number | undefined
  >();

  const [appointmentTypeId, setAppointmentTypeId] = useState<
    number | undefined
  >();

  const [staffMemberId, setStaffMemberId] = useState<number | undefined>();

  const setupAppointmentTypes = useCallback(
    (
      date: Date | string,
      startTime: Date,
      endTime: Date,
      appointmentTypeIdParam: number,
      staffMemberId: number
    ) => {
      setAppointmentTypeId(appointmentTypeIdParam);
      const appointmentDate = isValid(new Date(date.toString()))
        ? new Date(date.toString())
        : null;

      if (
        appointmentDate &&
        isValid(appointmentDate) &&
        isValid(startTime) &&
        isValid(endTime)
      ) {
        const newStartTime = getDateTime(appointmentDate, startTime);
        const newEndTime = getDateTime(appointmentDate, endTime);

        setAppointmentStartDate(newStartTime.getTime());
        setAppointmentEndDate(newEndTime.getTime());
      }
      setStaffMemberId(staffMemberId);
    },
    []
  );

  const intersectingBlocks = useMemo(
    () =>
      !appointmentStartDate || !appointmentEndDate || !appointmentTypeId
        ? []
        : appointments.filter(
            (appointment: any) =>
              appointment.type === "Block" &&
              appointment.staffMemberId === staffMemberId &&
              ((isWithinInterval(new Date(appointmentStartDate), {
                start: new Date(appointment.startDate),
                end: new Date(appointment.endDate),
              }) &&
                !isEqual(
                  new Date(appointmentStartDate),
                  new Date(appointment.endDate)
                )) ||
                (isWithinInterval(new Date(appointmentEndDate), {
                  start: new Date(appointment.startDate),
                  end: new Date(appointment.endDate),
                }) &&
                  !isEqual(
                    new Date(appointmentEndDate),
                    new Date(appointment.startDate)
                  )))
          ),
    [
      appointmentTypeId,
      appointments,
      appointmentStartDate,
      appointmentEndDate,
      staffMemberId,
    ]
  );

  const result = useMemo(() => {
    return {
      isReserved: Boolean(
        intersectingBlocks.find(
          (block: any) => block.appointmentType?.id !== appointmentTypeId
        )
      ),
      intersectingBlocks,
      setupAppointmentTypes,
    };
  }, [appointmentTypeId, intersectingBlocks, setupAppointmentTypes]);

  return (
    <ReservedAppointmentTypesContext.Provider value={result}>
      {children}
    </ReservedAppointmentTypesContext.Provider>
  );
}

export default function useReservedAppointmentTypes() {
  const context = useContext(ReservedAppointmentTypesContext);
  if (!context) {
    throw new Error(
      `useReservedAppointmentTypes must be used within a ReservedAppointmentTypesProvider`
    );
  }

  const { isReserved, intersectingBlocks, setupAppointmentTypes } = context;
  return {
    isReserved,
    intersectingBlocks,
    setupAppointmentTypes,
  };
}
